import { inject, Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import {
  ICompositionSuggestion,
  IWarning,
  SmartSuggestionMetaStateModel,
} from './smart-suggestion.model'
import {
  ResetSmartSuggestionState,
  GenerateKitCompositionsAction,
  AddWarningAction,
  OpenWarningModalAction,
  CloseWarningModalAction,
  RemoveWarningAction,
} from './smart-suggestion.meta.actions'
import { BracketsHelper } from './helpers/brackets.helper'
import { ProtectionsHelper } from './helpers/protections.helper'
import { ConnectorsHelper } from './helpers/connectors.helper'
import { InvertersHelper } from './helpers/inverters.helper'
import { CablesHelper } from './helpers/cables.helper'
import { PanelsHelper } from './helpers/panels.helper'
import { AccessoriesHelper } from './helpers/accessories.helper'

const INITIAL_STATE: SmartSuggestionMetaStateModel = {
  isAlertOpen: false,
  warnings: [],
  isWarningModalOpen: false,
  version: '2.0.0-aurora.0',
}

@State<SmartSuggestionMetaStateModel>({
  name: 'smartSuggestionMeta',
  defaults: { ...INITIAL_STATE },
})
@Injectable()
export class SmartSuggestionMetaState {
  private bracketsHelper = inject(BracketsHelper)
  private protectionsHelper = inject(ProtectionsHelper)
  private connectorsHelper = inject(ConnectorsHelper)
  private invertersHelper = inject(InvertersHelper)
  private cablesHelper = inject(CablesHelper)
  private panelsHelper = inject(PanelsHelper)
  private accessoriesHelper = inject(AccessoriesHelper)

  @Selector()
  public static getState(
    state: SmartSuggestionMetaStateModel,
  ): SmartSuggestionMetaStateModel {
    return state
  }

  @Selector()
  public static getIsAlertOpen(state: SmartSuggestionMetaStateModel): boolean {
    return state.isAlertOpen
  }

  @Selector()
  public static getWarnings(state: SmartSuggestionMetaStateModel): IWarning[] {
    return state.warnings
  }

  @Selector()
  public static getIsWarningModalOpen(
    state: SmartSuggestionMetaStateModel,
  ): boolean {
    return state.isWarningModalOpen
  }

  @Selector()
  public static getCompositionSuggestions(
    state: SmartSuggestionMetaStateModel,
  ): ICompositionSuggestion {
    return state.compositionSuggestions
  }

  @Action(ResetSmartSuggestionState)
  resetState(ctx: StateContext<SmartSuggestionMetaStateModel>): void {
    ctx.setState({ ...INITIAL_STATE })
  }

  @Action(RemoveWarningAction)
  removeWarning(
    ctx: StateContext<SmartSuggestionMetaStateModel>,
    action: RemoveWarningAction,
  ): void {
    const state = ctx.getState()
    const { warnings } = state
    const updatedWarnings = warnings.filter(
      (warning) => warning.reference !== action.payload,
    )
    ctx.patchState({ warnings: updatedWarnings })
  }

  @Action(AddWarningAction)
  addWarning(
    ctx: StateContext<SmartSuggestionMetaStateModel>,
    action: AddWarningAction,
  ): void {
    const state = ctx.getState()
    const { warnings } = state
    const foundByReference = warnings.find(
      (warning) => warning.reference === action.payload.reference,
    )
    let updatedWarnings = []
    if (foundByReference) {
      updatedWarnings = warnings.filter(
        (warning) => warning.reference !== action.payload.reference,
      )
      updatedWarnings = [...updatedWarnings, action.payload]
    } else {
      updatedWarnings = [...warnings, action.payload]
    }
    ctx.patchState({ warnings: updatedWarnings })
  }

  @Action(OpenWarningModalAction)
  openWarningModal(ctx: StateContext<SmartSuggestionMetaStateModel>): void {
    ctx.patchState({ isWarningModalOpen: true })
  }

  @Action(CloseWarningModalAction)
  closeWarningModal(ctx: StateContext<SmartSuggestionMetaStateModel>): void {
    ctx.patchState({ isWarningModalOpen: false })
  }

  @Action(GenerateKitCompositionsAction)
  generateKitCompositions(ctx: StateContext<SmartSuggestionMetaStateModel>) {
    const panelsSuggestions = this.panelsHelper.getPanelsSuggestions()
    const invertersSuggestions = this.invertersHelper.getInvertersSuggestions()
    const bracketsSuggestions = this.bracketsHelper.getBracketsSuggestions()
    const protectionsSuggestions =
      this.protectionsHelper.getProtectionsSuggestions()
    const connectorsSuggestions =
      this.connectorsHelper.getConnectorsSuggestions()
    const cablesSuggestions = this.cablesHelper.getCablesSuggestions()
    const accessoriesSuggestions =
      this.accessoriesHelper.getAccessoriesSuggestions()
    const compositionSuggestions = {
      panels: panelsSuggestions,
      inverters: invertersSuggestions,
      brackets: bracketsSuggestions,
      protections: protectionsSuggestions,
      connectors: connectorsSuggestions,
      cables: cablesSuggestions,
      accessories: accessoriesSuggestions,
    }
    console.warn('COMPOSITION SUGGESTIONS', compositionSuggestions)
    ctx.patchState({ compositionSuggestions })
  }
}
